import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FeatureFlag } from '@mode/shared/contract-common';
import { SharedDataAccessWebappModule } from '@mode/shared/data-access-webapp';
import { getInitialFlagValue, MetaTagsService } from '@mode/shared/util-js';
import { AnalyticsService } from '@mode/shared/util-segment';
import {
  DataViewFacade,
  FlamingoFacade,
  ImportedDatasetFacade,
  PlanMetadataFacade,
  SwitchAttrsFacade,
  SwitchViewFacade,
  SwitchViewReorderingFacade,
} from '@mode/switch-views/contract';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { DataViewService } from './data-view.service';
import * as fromDatasetResult from './dataset-result/dataset-result.reducer';
import { FlamingoFacadeService } from './flamingo-facade.service';
import { FlamingoEffects, MAX_FLAMINGO_REQUESTS } from './flamingo/flamingo.effects';
import * as fromFlamingo from './flamingo/flamingo.reducer';
import * as fromPlan from './flamingo/plan.reducer';
import { ImportedDatasetService } from './imported-dataset/imported-dataset.service';
import { PlanMetadataService } from './plan-metadata.service';
import * as fromPlanMetadata from './plan-metadata/plan-metadata.reducer';
import { SwitchAttrsEffects } from './switch-attrs/switch-attrs.effects';
import * as fromSwitchAttrs from './switch-attrs/switch-attrs.reducer';
import { SwitchAttrsService } from './switch-attrs/switch-attrs.service';
import { SwitchViewEffects } from './switch-view/switch-view.effects';
import * as fromSwitchView from './switch-view/switch-view.reducer';
import { SwitchViewService } from './switch-view/switch-view.service';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { SwitchViewsSwitchAttrsDomainModule } from '@mode/switch-views/switch-attrs/domain';
import { SwitchViewReorderingService } from './switch-view/switch-view-reordering.service';
import { SwitchViewReorderingTrackingService } from './switch-view/switch-view-reordering-tracking.service';

@NgModule({
  imports: [
    CommonModule,
    SharedDataAccessWebappModule,
    SwitchViewsSwitchAttrsDomainModule,
    StoreModule.forFeature(fromFlamingo.flamingoFeatureKey, fromFlamingo.reducer),
    StoreModule.forFeature(fromPlan.planFeatureKey, fromPlan.reducer),
    StoreModule.forFeature(fromSwitchAttrs.switchAttrsFeatureKey, fromSwitchAttrs.reducer),
    StoreModule.forFeature(fromSwitchView.switchViewFeatureKey, fromSwitchView.reducer),
    EffectsModule.forFeature([FlamingoEffects, SwitchAttrsEffects, SwitchViewEffects]),
    StoreModule.forFeature(fromDatasetResult.datasetResultFeatureKey, fromDatasetResult.reducer),
    StoreModule.forFeature(fromPlanMetadata.planMetadataFeatureKey, fromPlanMetadata.reducer),
  ],
  providers: [
    {
      /**
       * Not using FeatureFlagsFacade in the relevant effects file because effects get
       * initialized before the constructor function is called. Since provider factory
       * cannot be async, using getInitialFlagValue here.
       */
      provide: MAX_FLAMINGO_REQUESTS,
      useFactory: () => getInitialFlagValue<number>(FeatureFlag.FlamingoRequestLimit) || 5,
    },
    { provide: DataViewFacade, useClass: DataViewService },
    { provide: SwitchViewFacade, useClass: SwitchViewService },
    { provide: FlamingoFacade, useClass: FlamingoFacadeService },
    { provide: PlanMetadataFacade, useClass: PlanMetadataService },
    { provide: SwitchAttrsFacade, useClass: SwitchAttrsService },
    { provide: ImportedDatasetFacade, useClass: ImportedDatasetService },
    {
      provide: SwitchViewReorderingFacade,
      useClass: SwitchViewReorderingTrackingService,
      deps: [SwitchViewReorderingService, AnalyticsService, MetaTagsService],
    },
  ],
})
export class SwitchViewsDomainModule {}
